import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B0C0E] text-[#F5F3EE]">
      <div className="hidden lg:flex relative overflow-hidden flex-col justify-between p-12 bg-[#131518] border-r border-[#26282C]">
        <div className="grid-bg absolute inset-0 opacity-30" />
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#D5FF3F]/10 blur-[120px]" />
        <Link to="/" className="relative flex items-center gap-2.5" data-testid="login-brand">
          <div className="w-8 h-8 rounded-sm bg-[#D5FF3F] flex items-center justify-center">
            <span className="font-display italic text-[#0B0C0E] text-lg leading-none">a</span>
          </div>
          <span className="font-display text-2xl">Aura Credit</span>
        </Link>
        <div className="relative">
          <div className="overline mb-6">member story</div>
          <blockquote>
            <p className="font-display text-4xl leading-tight">
              "Three collections gone in six weeks. I didn't draft a single letter — Aura wrote them, I just signed."
            </p>
            <footer className="mt-6 text-sm text-[#A8ADA5] font-mono uppercase tracking-widest">— Rachel M. · +112 pts</footer>
          </blockquote>
        </div>
        <div className="relative text-xs text-[#6D716C] font-mono uppercase tracking-widest">
          Secure by design · No tracking beacons
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <div className="overline mb-3">Welcome back</div>
            <h1 className="font-display text-5xl">Sign in.</h1>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-sm h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-sm h-11"
              />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full h-12 rounded-full btn-lime"
          >
            {loading ? "Signing in…" : <>Sign in <ArrowUpRight size={15} className="ml-1.5" /></>}
          </Button>
          <div className="text-sm text-[#A8ADA5] text-center">
            New here?{" "}
            <Link to="/register" className="text-[#D5FF3F] underline underline-offset-4" data-testid="login-to-register">
              Create an account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
